import {
  PathsV1VehicleValuationUuidGetParametersQueryExtend,
  VehicleValuationErrorProducer,
  VehicleValuationProvider,
  VehicleValuationStatus,
  VehicleValuationType,
  VehicleValuationWarningCode,
} from "@/api-schema-enums";
import { Can, useAbility } from "@/casl-provider";
import { useVehicleValuationByUuidQuery } from "@/common/api/vehicle-valuation/use-vehicle-valuation-by-uuid.query";
import { useVehicleValuationCopyForReevaluationMutation } from "@/common/api/vehicle-valuation/use-vehicle-valuation-copy-for-reevaluation.mutation";
import { useVehicleValuationResetConfirmationAndAssignmentMutation } from "@/common/api/vehicle-valuation/use-vehicle-valuation-reset-confirmation-and-assignment.mutation";
import { useVehicleValuationStartConfirmationMutation } from "@/common/api/vehicle-valuation/use-vehicle-valuation-start-confirmation.mutation";
import { useVehicleValuationSyncDatDataMutation } from "@/common/api/vehicle-valuation/use-vehicle-valuation-sync-dat-data.mutation";
import { useVehicleValuationSyncV1GewaMutation } from "@/common/api/vehicle-valuation/use-vehicle-valuation-sync-v1-gewa.mutation";
import { useVehicleValuationVehicleDataRepairStatusQuery } from "@/common/api/vehicle-valuation/use-vehicle-valuation-vehicle-data-repair-status.query";
import { useVehicleValuationVehicleDataRepairMutation } from "@/common/api/vehicle-valuation/use-vehicle-valuation-vehicle-data-repair.mutation";
import { canVehicleValuationBeConfirmed } from "@/common/helpers/can-vehicle-valuation-be-confirmed";
import { canVehicleValuationBeStarted } from "@/common/helpers/can-vehicle-valuation-be-started";
import { canVehicleValuationSelectVehicle } from "@/common/helpers/can-vehicle-valuation-select-vehicle";
import { formatApiDateOnlyToLocale } from "@/common/helpers/format-date";
import { isVehicleValuationValid } from "@/common/helpers/is-vehicle-valuation-valid";
import { openInNewTab } from "@/common/helpers/open-in-new-tab";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { useAppFeatureFlag } from "@/common/hooks/use-app-feature-flag";
import { BrandLogo, BrandLogoVariant } from "@/components/brand-logo/brand-logo";
import { CommandButton } from "@/components/command-palette/command-button";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { CenteredLayout } from "@/components/layouts/centered-layout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyValue } from "@/components/ui/empty-value";
import { UserItem, UserItemVariant } from "@/components/user-item/user-item";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { VehicleInfoCard } from "@/components/vehicle-info-card/vehicle-info-card";
import { VehicleSnapshotAction } from "@/components/vehicle-snapshot/vehicle-snapshot-action";
import { VehicleValuationDateIndicator } from "@/components/vehicle-valuation/valuation-date-indicator";
import { VehicleValuationMileageDisplay } from "@/components/vehicle-valuation/vehicle-valuation-mileage-display";
import { VehicleValuationProviderBadge } from "@/components/vehicle-valuation/vehicle-valuation-provider-badge";
import { VehicleValuationReimportDisplay } from "@/components/vehicle-valuation/vehicle-valuation-reimport-display";
import { VehicleValuationSnapshotAction } from "@/components/vehicle-valuation/vehicle-valuation-snapshot-action";
import { VehicleValuationStatusBadge } from "@/components/vehicle-valuation/vehicle-valuation-status-badge";
import { FeatureFlag } from "@/config/feature-flags";
import { getImpliedConfirmedNetValue } from "@/modules/fab/vehicle-valuation/_helpers/get-implied-confirmed-net-value";
import { getVehicleValuationRoute } from "@/modules/fab/vehicle-valuation/_helpers/get-vehicle-valuation-route";
import { roundVehicleValuationPriceForDisplay } from "@/modules/fab/vehicle-valuation/_helpers/vehicle-valuation-derived-values";
import {
  getVehicleAccidentStateLabel,
  getVehicleBooleanLabel,
  getVehicleValuationRimTypeLabel,
  getVehicleValuationTypeLabel,
  shouldShowVehicleAccidentDetails,
} from "@/modules/fab/vehicle-valuation/_helpers/vehicle-valuation-metadata";
import { VEHICLE_VALUATION_SNAPSHOT_SUBJECT } from "@/modules/fab/vehicle-valuation/vehicle-valuation.permissions";
import { VEHICLE_SNAPSHOT_SUBJECT } from "@/modules/masterdata/vehicle/vehicle-snapshot.permissions";
import { useAnimate } from "framer-motion";
import {
  AlertTriangle,
  CarFrontIcon,
  DownloadIcon,
  ExternalLinkIcon,
  HandCoinsIcon,
  RefreshCwIcon,
  TriangleAlert,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import { DetailInfoRow } from "./_components/detail-info-row";
import { ValuationComparisonRow } from "./_components/valuation-comparison-row";
import { VehicleValuationActionGroup } from "./_components/vehicle-valuation-action-group";
import { VehicleValuationBuyInsCard } from "./_components/vehicle-valuation-buy-ins-card";
import { VehicleValuationExpiryTooltip } from "./_components/vehicle-valuation-expiry-tooltip";
import { VehicleValuationV1GewaSyncDialog } from "./_components/vehicle-valuation-v1-gewa-sync-dialog";
import { VehicleValuationVehicleDataRepairButton } from "./_components/vehicle-valuation-vehicle-data-repair-button";
import { resolveVehicleValuationFailureDisplay } from "./_helpers/vehicle-valuation-failure-display";
import { vehicleRequiresBranchSelectionForV1GewaSync } from "./_helpers/vehicle-valuation-v1-gewa-sync";

interface VehicleValuationDetailParams {
  uuid: string;
}

const DAT_MYCLAIM_RND = "16293";

function buildDatClaimUrl(datClaimId: string): string {
  return `https://www.dat.de/myClaim/inbox.jsp?rnd=${DAT_MYCLAIM_RND}#claim?subView=bundle_landing_page&claim=${encodeURIComponent(datClaimId)}`;
}

export function VehicleValuationDetailView() {
  return (
    <Can I="read" a="VehicleValuation">
      <VehicleValuationDetailContent />
    </Can>
  );
}

function VehicleValuationDetailContent() {
  const { t } = useTranslation(["common", "masterdata", "fab"]);
  const [_, navigate] = useLocation();
  const ability = useAbility();
  const { value: isV1SyncEnabled } = useAppFeatureFlag(FeatureFlag.FabV1Sync);
  const { value: isVehicleBuyInEnabled } = useAppFeatureFlag(FeatureFlag.IsVehicleBuyInEnabled);
  const params = useParams<VehicleValuationDetailParams>();
  const uuid = params.uuid;
  const canManageVehicleValuation = ability.can("manage", "VehicleValuation");
  const { mutateAsync: copyForReevaluationMutateAsync, isPending: isCopyForReevaluationPending } =
    useVehicleValuationCopyForReevaluationMutation();
  const startConfirmationMutation = useVehicleValuationStartConfirmationMutation();
  const resetConfirmationAndAssignmentMutation = useVehicleValuationResetConfirmationAndAssignmentMutation();
  const syncDatDataMutation = useVehicleValuationSyncDatDataMutation();
  const syncV1GewaMutation = useVehicleValuationSyncV1GewaMutation();
  const vehicleDataRepairMutation = useVehicleValuationVehicleDataRepairMutation();
  const vehicleDataRepairStatusQuery = useVehicleValuationVehicleDataRepairStatusQuery(uuid, canManageVehicleValuation);
  const [isSyncV1GewaDialogOpen, setIsSyncV1GewaDialogOpen] = useState(false);
  const [syncIconRef, animateSyncIcon] = useAnimate();

  const handleStartConfirmation = useCallback(async () => {
    if (!uuid) return;

    await toastMutationStatus({
      mutationFn: () => startConfirmationMutation.mutateAsync({ uuid }),
      successMessage: t("masterdata:VehicleValuationConfirmationStarted"),
      errorMessage: t("masterdata:ErrorStartingVehicleValuationConfirmation"),
      onSuccess: () => navigate(`~/fab/list/${uuid}/confirm`),
    });
  }, [uuid, startConfirmationMutation, navigate, t]);

  const handleSyncDatData = useCallback(async () => {
    if (!uuid) return;

    await toastMutationStatus({
      mutationFn: () => syncDatDataMutation.mutateAsync({ uuid }),
      successMessage: t("fab:VehicleValuationDatDataSynced"),
      errorMessage: t("fab:ErrorSyncingVehicleValuationDatData"),
    });
  }, [uuid, syncDatDataMutation, t]);

  const handleRepairVehicleData = useCallback(async () => {
    if (!uuid) return;

    await toastMutationStatus({
      mutationFn: () => vehicleDataRepairMutation.mutateAsync({ uuid }),
      successMessage: t("fab:VehicleDataRepaired"),
      errorMessage: t("fab:ErrorRepairingVehicleData"),
    });
  }, [uuid, vehicleDataRepairMutation, t]);

  const vehicleValuationQuery = useVehicleValuationByUuidQuery(uuid, {
    parameters: {
      extend: [
        PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle,
        PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_ModelGeneration,
        PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily,
        PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily_Brand,
        PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_OwningBranch,
        PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_CurrentBranch,
        PathsV1VehicleValuationUuidGetParametersQueryExtend.ConfirmedBy,
        PathsV1VehicleValuationUuidGetParametersQueryExtend.Requester,
        PathsV1VehicleValuationUuidGetParametersQueryExtend.AssignedTo,
        PathsV1VehicleValuationUuidGetParametersQueryExtend.Webscan,
      ],
    },
  });

  const valuation = vehicleValuationQuery.data;

  useEffect(() => {
    if (valuation?.status === VehicleValuationStatus.VehicleSelectionRequired) {
      navigate(getVehicleValuationRoute(valuation), { replace: true });
    }
  }, [navigate, valuation]);

  const brand = valuation?.vehicle?.modelGeneration?.modelFamily?.brand;
  const shouldShowAccidentDetails = shouldShowVehicleAccidentDetails(valuation?.accidentState);
  const canReadVehicleSnapshot = ability.can("read", VEHICLE_SNAPSHOT_SUBJECT);
  const canReadVehicleValuationSnapshot = ability.can("read", VEHICLE_VALUATION_SNAPSHOT_SUBJECT);
  const canStartVehicleValuationConfirmation =
    canManageVehicleValuation && canVehicleValuationBeStarted(valuation?.status);
  const canContinueVehicleValuationConfirmation =
    canManageVehicleValuation && canVehicleValuationBeConfirmed(valuation?.status);
  const canStartVehicleReevaluation =
    canManageVehicleValuation && valuation?.status === VehicleValuationStatus.Confirmed;
  const canCreateVehicleBuyIn =
    isVehicleBuyInEnabled &&
    ability.can("create", "VehicleBuyIn") &&
    valuation?.status === VehicleValuationStatus.Confirmed;
  const canReadVehicleBuyIn = isVehicleBuyInEnabled && ability.can("read", "VehicleBuyIn");
  const canSyncVehicleValuationToV1Gewa =
    isV1SyncEnabled && canManageVehicleValuation && valuation?.status === VehicleValuationStatus.Confirmed;
  const isValuationExpired = valuation !== undefined && !isVehicleValuationValid(valuation.validUntil);
  const canOpenVehicleValuationReport = Boolean(valuation?.reportUrl);
  const isConfirmedValuation = valuation?.status === VehicleValuationStatus.Confirmed;
  const isFailedValuation = valuation?.status === VehicleValuationStatus.Failed;
  const canOpenDatCase = canManageVehicleValuation && Boolean(valuation?.datClaimId) && !isConfirmedValuation;
  const canSyncDatData = canManageVehicleValuation && Boolean(valuation?.datClaimId) && !isConfirmedValuation;
  const shouldShowVehicleDataRepair = canManageVehicleValuation && Boolean(vehicleDataRepairStatusQuery.data?.needed);
  const canSelectVehicle =
    valuation?.provider === VehicleValuationProvider.Dat &&
    Boolean(valuation.hasVehicleSelectionSnapshot) &&
    valuation.status !== VehicleValuationStatus.VehicleSelectionRequired &&
    canVehicleValuationSelectVehicle(valuation.status, {
      canCreate: false,
      canManage: canManageVehicleValuation,
    });
  const valuationUser = isConfirmedValuation
    ? (valuation?.confirmedBy ?? valuation?.assignedTo)
    : valuation?.assignedTo;
  const valuationUserLabel =
    isConfirmedValuation && valuation?.confirmedBy ? t("masterdata:ConfirmedBy") : t("masterdata:AssignedTo");
  const shouldShowLeaseBuyoutAmount =
    isConfirmedValuation && valuation?.valuationType === VehicleValuationType.LeasingBuyout;
  const shouldShowVehicleValuationType = isConfirmedValuation;
  const shouldShowConfirmedValuationAmounts = isConfirmedValuation && !shouldShowLeaseBuyoutAmount;
  const shouldShowAdditionalCostValues =
    valuation?.valuationType !== VehicleValuationType.LeasingBuyout &&
    valuation?.valuationType !== VehicleValuationType.WholesaleBuyer;
  const shouldRenderWorkflowActions =
    canStartVehicleValuationConfirmation ||
    canContinueVehicleValuationConfirmation ||
    (canSelectVehicle && !isFailedValuation) ||
    canStartVehicleReevaluation ||
    canCreateVehicleBuyIn;
  const shouldRenderExternalSystemActions = canSyncVehicleValuationToV1Gewa || canOpenDatCase || canSyncDatData;
  const shouldRenderDocumentActions =
    canOpenVehicleValuationReport ||
    shouldShowVehicleDataRepair ||
    (canReadVehicleValuationSnapshot && Boolean(valuation) && !isFailedValuation);
  const shouldRenderActions =
    !isFailedValuation &&
    (shouldRenderWorkflowActions || shouldRenderExternalSystemActions || shouldRenderDocumentActions);
  const failureDisplay = resolveVehicleValuationFailureDisplay(
    valuation?.errorProducer,
    valuation?.errorCode,
    valuation?.errorMessage,
    {
      [VehicleValuationErrorProducer.Dat]: t("fab:VehicleValuationDatErrorCodeUnavailable"),
      [VehicleValuationErrorProducer.Aws]: t("fab:VehicleValuationAwsErrorDetailUnavailable"),
      [VehicleValuationErrorProducer.Alphartis]: t("fab:VehicleValuationAlphartisErrorDetailUnavailable"),
    },
  );
  const warningMessage =
    valuation?.warningCode === VehicleValuationWarningCode.DatVinBasic
      ? t("fab:VehicleValuationDatVinBasicWarning")
      : null;

  const impliedConfirmedTradeNet = getImpliedConfirmedNetValue({
    originalNet: valuation?.tradeNet,
    originalGross: valuation?.tradeGross,
    confirmedGross: valuation?.confirmedTradeGross,
  });
  const impliedConfirmedSalesNet = getImpliedConfirmedNetValue({
    originalNet: valuation?.salesNet,
    originalGross: valuation?.salesGross,
    confirmedGross: valuation?.confirmedSalesGross,
  });
  const impliedConfirmedMarginNet = getImpliedConfirmedNetValue({
    originalNet: valuation?.marginNet,
    originalGross: valuation?.marginGross,
    confirmedGross: valuation?.confirmedMarginGross,
  });
  const roundedConfirmedTradeGross = roundVehicleValuationPriceForDisplay(valuation?.confirmedTradeGross);
  const roundedConfirmedSalesGross = roundVehicleValuationPriceForDisplay(valuation?.confirmedSalesGross);
  const roundedConfirmedMarginGross = roundVehicleValuationPriceForDisplay(valuation?.confirmedMarginGross);
  const roundedImpliedConfirmedTradeNet = roundVehicleValuationPriceForDisplay(impliedConfirmedTradeNet);
  const roundedImpliedConfirmedSalesNet = roundVehicleValuationPriceForDisplay(impliedConfirmedSalesNet);
  const roundedImpliedConfirmedMarginNet = roundVehicleValuationPriceForDisplay(impliedConfirmedMarginNet);

  const modelName =
    valuation?.vehicle?.modelDescription ||
    valuation?.vehicle?.modelGeneration?.name ||
    valuation?.vehicle?.modelGeneration?.modelFamily?.name;
  const vehicleName = [brand?.name, modelName].filter(Boolean).join(" ") || t("masterdata:VehicleValuationDetails");

  const title = brand ? (
    <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight sm:text-3xl">
      <BrandLogo variant={BrandLogoVariant.MEDIUM} brand={brand} />
      <span>{vehicleName}</span>
    </h1>
  ) : (
    vehicleName
  );

  const handleStartReevaluation = async (): Promise<void> => {
    if (!valuation?.uuid) {
      return;
    }

    let copiedValuationUuid: string | undefined;

    await toastMutationStatus({
      mutationFn: async () => {
        const copiedValuation = await copyForReevaluationMutateAsync({
          uuid: valuation.uuid,
        });

        await startConfirmationMutation.mutateAsync({
          uuid: copiedValuation.uuid,
        });

        copiedValuationUuid = copiedValuation.uuid;
        return copiedValuation;
      },
      successMessage: t("fab:VehicleReevaluationCreated"),
      errorMessage: t("fab:ErrorCreatingVehicleReevaluation"),
    });

    if (copiedValuationUuid) {
      navigate(`~/fab/list/${copiedValuationUuid}/confirm`);
    }
  };

  const handleSyncV1Gewa = async (branchUuid?: string): Promise<boolean> => {
    if (!valuation?.uuid) {
      return false;
    }

    void animateSyncIcon(syncIconRef.current, { rotate: 360 }, { duration: 0.4, ease: "easeInOut" }).then(() => {
      void animateSyncIcon(syncIconRef.current, { rotate: 0 }, { duration: 0 });
    });

    let didSync = false;

    await toastMutationStatus({
      mutationFn: async () => {
        await syncV1GewaMutation.mutateAsync({
          uuid: valuation.uuid,
          branchUuid,
        });
        didSync = true;
      },
      successMessage: t("fab:VehicleValuationV1GewaSyncSuccess"),
      errorMessage: t("fab:VehicleValuationV1GewaSyncError"),
    });

    return didSync;
  };

  return (
    <UuidExistenceWrapper
      uuid={uuid}
      query={useVehicleValuationByUuidQuery}
      queryResult={vehicleValuationQuery}
      notFoundProps={{
        title: t("masterdata:VehicleValuationNotFound"),
        message: t("masterdata:RequestedVehicleValuationDoesNotExist"),
        description: t("masterdata:PleaseCheckVehicleValuationOrContactSupport"),
        backTo: "~/fab/list",
        backToLabel: t("masterdata:BackToVehicleValuations"),
      }}
    >
      <CenteredLayout title={title} subTitle={t("masterdata:VehicleValuationDetails")}>
        <div
          className={
            isFailedValuation
              ? "relative h-[calc(100svh-12rem)] max-h-[calc(100svh-12rem)] overflow-hidden"
              : "relative min-h-[calc(100svh-12rem)]"
          }
        >
          <div
            aria-hidden={isFailedValuation}
            inert={isFailedValuation}
            className={
              isFailedValuation
                ? "pointer-events-none flex flex-col gap-6 opacity-50 select-none"
                : "flex flex-col gap-6"
            }
          >
            {valuation?.vehicle ? (
              <VehicleInfoCard
                headerActions={
                  canReadVehicleSnapshot && valuation.vehicle.uuid ? (
                    <VehicleSnapshotAction
                      size={ButtonSize.Sm}
                      vehicleUuid={valuation.vehicle.uuid}
                      showInCommandPalette={!isFailedValuation}
                    />
                  ) : undefined
                }
                vehicle={valuation.vehicle}
              />
            ) : null}

            {canManageVehicleValuation && valuation?.webscan ? (
              <Card>
                <CardHeader>
                  <CardTitle>{t("masterdata:VehicleValuationWebscan")}</CardTitle>
                  <CardDescription>{t("masterdata:VehicleValuationWebscanDescription")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <DetailInfoRow
                      label={t("masterdata:WebscanTotalHits")}
                      value={automaticCellRenderer(valuation.webscan.totalHits)}
                    />
                    <DetailInfoRow
                      label={t("masterdata:WebscanSalesPriceMin")}
                      value={automaticCellRenderer(valuation.webscan.salesPriceMin, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                    />
                    <DetailInfoRow
                      label={t("masterdata:WebscanSalesPriceMax")}
                      value={automaticCellRenderer(valuation.webscan.salesPriceMax, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                    />
                    <DetailInfoRow
                      label={t("masterdata:WebscanSalesPriceAverage")}
                      value={automaticCellRenderer(valuation.webscan.salesPriceAverage, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                    />
                    <DetailInfoRow
                      label={t("masterdata:WebscanMileageAverage")}
                      value={automaticCellRenderer(valuation.webscan.mileageAverage)}
                    />
                    <DetailInfoRow
                      label={t("masterdata:WebscanDaysOnMarketPlaceAverage")}
                      value={automaticCellRenderer(valuation.webscan.daysOnMarketPlaceAverage)}
                    />
                  </div>
                </CardContent>
              </Card>
            ) : null}

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <Card>
                <CardHeader>
                  <CardTitle>{t("masterdata:VehicleValuationInputs")}</CardTitle>
                  <CardDescription>{t("masterdata:VehicleValuationInputsDescription")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <DetailInfoRow
                      label={t("common:Status")}
                      value={<VehicleValuationStatusBadge status={valuation?.status} />}
                    />
                    <DetailInfoRow
                      label={t("masterdata:Provider")}
                      value={<VehicleValuationProviderBadge provider={valuation?.provider} />}
                    />
                    <DetailInfoRow
                      label={t("masterdata:Requester")}
                      value={
                        valuation?.requester ? (
                          <UserItem user={valuation.requester} variant={UserItemVariant.Compact} />
                        ) : undefined
                      }
                    />
                    <DetailInfoRow
                      label={t("common:CreatedAt")}
                      value={automaticCellRenderer(valuation?.createdAt, {
                        dateFormat: "datetime",
                      })}
                    />
                    <DetailInfoRow
                      label={t("common:ValidUntil")}
                      value={automaticCellRenderer(valuation?.validUntil, { dateFormat: "datetime" })}
                    />
                    <DetailInfoRow label={t("fab:VIN")} value={valuation?.vinUsed ?? <EmptyValue />} mono />
                    {canManageVehicleValuation && valuation?.datClaimId ? (
                      <DetailInfoRow label={t("fab:VehicleValuationDatClaimId")} value={valuation.datClaimId} mono />
                    ) : null}
                  </div>

                  <div className="grid gap-4 border-t pt-4 sm:grid-cols-2">
                    <DetailInfoRow
                      label={t("common:RegistrationDate")}
                      value={formatApiDateOnlyToLocale(valuation?.registrationDateUsed)}
                    />
                    <DetailInfoRow
                      label={t("fab:VehicleValuationTargetDate")}
                      value={
                        <div className="flex items-center gap-2">
                          <span>{formatApiDateOnlyToLocale(valuation?.valuationDateUsed) ?? <EmptyValue />}</span>
                          <VehicleValuationDateIndicator
                            value={valuation?.valuationDateUsed}
                            referenceDate={valuation?.createdAt}
                            reserveSpace
                          />
                        </div>
                      }
                    />
                    <DetailInfoRow
                      label={t("masterdata:Mileage")}
                      value={
                        <VehicleValuationMileageDisplay
                          mileage={valuation?.mileageUsed}
                          projectedMileage={valuation?.projectedMileage}
                          targetDate={valuation?.valuationDateUsed}
                        />
                      }
                    />
                    <DetailInfoRow
                      label={t("masterdata:NumberOfHolders")}
                      value={automaticCellRenderer(valuation?.numberOfHoldersUsed)}
                    />
                    <DetailInfoRow
                      label={t("fab:RimType")}
                      value={getVehicleValuationRimTypeLabel(valuation?.rimType, t)}
                    />
                    <DetailInfoRow
                      label={t("masterdata:VatDeductible")}
                      value={getVehicleBooleanLabel(valuation?.vatDeductible, t)}
                    />
                    <DetailInfoRow
                      label={t("masterdata:Reimport")}
                      value={<VehicleValuationReimportDisplay reimport={valuation?.reimport} />}
                    />
                  </div>

                  <div className="grid gap-4 border-t pt-4 sm:grid-cols-2">
                    <DetailInfoRow
                      label={t("masterdata:AccidentState")}
                      value={getVehicleAccidentStateLabel(valuation?.accidentState, t)}
                    />
                    {shouldShowAccidentDetails ? (
                      <DetailInfoRow
                        label={t("fab:AccidentValue")}
                        value={automaticCellRenderer(valuation?.accidentValue, {
                          numberFormat: "currency",
                          numberDecimals: 2,
                        })}
                      />
                    ) : null}
                    {shouldShowAccidentDetails ? (
                      <DetailInfoRow
                        label={t("fab:AccidentDescription")}
                        fullWidth
                        value={
                          valuation?.accidentDescription ? (
                            <div className="wrap-break-word whitespace-pre-wrap">{valuation.accidentDescription}</div>
                          ) : undefined
                        }
                      />
                    ) : null}
                  </div>

                  <div className="grid gap-4 border-t pt-4 sm:grid-cols-2">
                    <DetailInfoRow
                      label={t("fab:EarlyLeaseTermination")}
                      value={getVehicleBooleanLabel(valuation?.earlyLeaseTermination, t)}
                    />
                    <DetailInfoRow
                      label={t("fab:EarlyLeaseTerminationAmount")}
                      value={automaticCellRenderer(valuation?.earlyLeaseTerminationAmount, {
                        numberFormat: "currency",
                        numberDecimals: 2,
                      })}
                    />
                  </div>

                  {valuation?.requesterNote ? (
                    <div className="grid gap-4 border-t pt-4 sm:grid-cols-2">
                      <DetailInfoRow
                        label={t("fab:VehicleValuationCreatorNote")}
                        fullWidth
                        value={<div className="wrap-break-word whitespace-pre-wrap">{valuation.requesterNote}</div>}
                      />
                    </div>
                  ) : null}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{t("masterdata:VehicleValuationResult")}</CardTitle>
                  <CardDescription>{t("masterdata:VehicleValuationResultDescription")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  {canManageVehicleValuation ? (
                    <>
                      <DetailInfoRow
                        label={t("fab:AppliedPriceAdjustment")}
                        value={automaticCellRenderer(valuation?.priceAdjustmentPercentage, {
                          numberFormat: "percentage",
                          numberDecimals: 2,
                        })}
                      />

                      <div className="space-y-2">
                        <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                          {t("masterdata:SalesGroup")}
                        </div>
                        <div className="space-y-2 border-l-2 pl-3">
                          <ValuationComparisonRow
                            label={t("masterdata:Gross")}
                            originalValue={automaticCellRenderer(valuation?.salesGross, {
                              numberFormat: "currency",
                              numberDecimals: 2,
                            })}
                            confirmedValue={automaticCellRenderer(roundedConfirmedSalesGross, {
                              numberFormat: "currency",
                              numberDecimals: 2,
                            })}
                            originalNumber={valuation?.salesGross}
                            confirmedNumber={
                              shouldShowConfirmedValuationAmounts ? roundedConfirmedSalesGross : undefined
                            }
                          />
                          <ValuationComparisonRow
                            label={t("masterdata:Net")}
                            originalValue={automaticCellRenderer(valuation?.salesNet, {
                              numberFormat: "currency",
                              numberDecimals: 2,
                            })}
                            confirmedValue={automaticCellRenderer(roundedImpliedConfirmedSalesNet, {
                              numberFormat: "currency",
                              numberDecimals: 2,
                            })}
                            originalNumber={valuation?.salesNet}
                            confirmedNumber={
                              shouldShowConfirmedValuationAmounts ? roundedImpliedConfirmedSalesNet : undefined
                            }
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                          {t("masterdata:MarginGroup")}
                        </div>
                        <div className="space-y-2 border-l-2 pl-3">
                          <ValuationComparisonRow
                            label={t("masterdata:Percentage")}
                            originalValue={automaticCellRenderer(valuation?.marginPercentage, {
                              numberFormat: "percentage",
                              numberDecimals: 2,
                            })}
                            confirmedValue={automaticCellRenderer(valuation?.confirmedMarginPercentage, {
                              numberFormat: "percentage",
                              numberDecimals: 2,
                            })}
                            originalNumber={valuation?.marginPercentage}
                            confirmedNumber={
                              shouldShowConfirmedValuationAmounts ? valuation?.confirmedMarginPercentage : undefined
                            }
                          />
                          <ValuationComparisonRow
                            label={t("masterdata:Gross")}
                            originalValue={automaticCellRenderer(valuation?.marginGross, {
                              numberFormat: "currency",
                              numberDecimals: 2,
                            })}
                            confirmedValue={automaticCellRenderer(roundedConfirmedMarginGross, {
                              numberFormat: "currency",
                              numberDecimals: 2,
                            })}
                            originalNumber={valuation?.marginGross}
                            confirmedNumber={
                              shouldShowConfirmedValuationAmounts ? roundedConfirmedMarginGross : undefined
                            }
                          />
                          <ValuationComparisonRow
                            label={t("masterdata:Net")}
                            originalValue={automaticCellRenderer(valuation?.marginNet, {
                              numberFormat: "currency",
                              numberDecimals: 2,
                            })}
                            confirmedValue={automaticCellRenderer(roundedImpliedConfirmedMarginNet, {
                              numberFormat: "currency",
                              numberDecimals: 2,
                            })}
                            originalNumber={valuation?.marginNet}
                            confirmedNumber={
                              shouldShowConfirmedValuationAmounts ? roundedImpliedConfirmedMarginNet : undefined
                            }
                          />
                        </div>
                      </div>

                      {shouldShowAdditionalCostValues ? (
                        <div className="space-y-2">
                          <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                            {t("masterdata:Other")}
                          </div>
                          <div className="space-y-2 border-l-2 pl-3">
                            <ValuationComparisonRow
                              label={t("masterdata:WarrantyGross")}
                              originalValue={automaticCellRenderer(valuation?.warranty, {
                                numberFormat: "currency",
                                numberDecimals: 2,
                              })}
                              confirmedValue={automaticCellRenderer(valuation?.confirmedWarranty, {
                                numberFormat: "currency",
                                numberDecimals: 2,
                              })}
                              originalNumber={valuation?.warranty}
                              confirmedNumber={
                                shouldShowConfirmedValuationAmounts ? valuation?.confirmedWarranty : undefined
                              }
                            />
                            <ValuationComparisonRow
                              label={t("masterdata:VehiclePreparationCostsGross")}
                              originalValue={automaticCellRenderer(valuation?.vehiclePreparationCosts, {
                                numberFormat: "currency",
                                numberDecimals: 2,
                              })}
                              confirmedValue={automaticCellRenderer(valuation?.confirmedVehiclePreparationCosts, {
                                numberFormat: "currency",
                                numberDecimals: 2,
                              })}
                              originalNumber={valuation?.vehiclePreparationCosts}
                              confirmedNumber={
                                shouldShowConfirmedValuationAmounts
                                  ? valuation?.confirmedVehiclePreparationCosts
                                  : undefined
                              }
                            />
                          </div>
                        </div>
                      ) : null}

                      <div className="space-y-2">
                        <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                          {t("masterdata:TradeGroup")}
                        </div>
                        <div className="space-y-2 border-l-2 pl-3">
                          <ValuationComparisonRow
                            label={t("masterdata:Gross")}
                            originalValue={automaticCellRenderer(valuation?.tradeGross, {
                              numberFormat: "currency",
                              numberDecimals: 2,
                            })}
                            confirmedValue={automaticCellRenderer(roundedConfirmedTradeGross, {
                              numberFormat: "currency",
                              numberDecimals: 2,
                            })}
                            originalNumber={valuation?.tradeGross}
                            confirmedNumber={
                              shouldShowConfirmedValuationAmounts ? roundedConfirmedTradeGross : undefined
                            }
                          />
                          <ValuationComparisonRow
                            label={t("masterdata:Net")}
                            originalValue={automaticCellRenderer(valuation?.tradeNet, {
                              numberFormat: "currency",
                              numberDecimals: 2,
                            })}
                            confirmedValue={automaticCellRenderer(roundedImpliedConfirmedTradeNet, {
                              numberFormat: "currency",
                              numberDecimals: 2,
                            })}
                            originalNumber={valuation?.tradeNet}
                            confirmedNumber={
                              shouldShowConfirmedValuationAmounts ? roundedImpliedConfirmedTradeNet : undefined
                            }
                          />
                        </div>
                      </div>

                      {shouldShowLeaseBuyoutAmount ? (
                        <div className="space-y-2">
                          <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                            {t("fab:VehicleValuationTypeLeasingBuyout")}
                          </div>
                          <div className="space-y-2 border-l-2 pl-3">
                            <DetailInfoRow
                              label={t("fab:LeaseBuyoutAmount")}
                              value={automaticCellRenderer(valuation?.leaseBuyoutAmount, {
                                numberFormat: "currency",
                                numberDecimals: 2,
                              })}
                              valueClassName={
                                valuation?.leaseBuyoutAmount != null ? "text-primary font-semibold" : undefined
                              }
                            />
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : isConfirmedValuation ? (
                    <DetailInfoRow
                      label={
                        shouldShowLeaseBuyoutAmount ? t("fab:LeaseBuyoutAmount") : t("masterdata:ConfirmedTradeGross")
                      }
                      value={automaticCellRenderer(
                        shouldShowLeaseBuyoutAmount ? valuation?.leaseBuyoutAmount : roundedConfirmedTradeGross,
                        {
                          numberFormat: "currency",
                          numberDecimals: 2,
                        },
                      )}
                      valueClassName={
                        shouldShowLeaseBuyoutAmount && valuation?.leaseBuyoutAmount != null
                          ? "text-primary font-semibold"
                          : undefined
                      }
                    />
                  ) : null}

                  {shouldShowVehicleValuationType ? (
                    <DetailInfoRow
                      label={t("fab:VehicleValuationType")}
                      value={getVehicleValuationTypeLabel(valuation?.valuationType, t)}
                    />
                  ) : null}
                  {isConfirmedValuation ? (
                    <DetailInfoRow
                      label={t("masterdata:ConfirmedAt")}
                      value={automaticCellRenderer(valuation?.confirmedAt, {
                        dateFormat: "datetime",
                      })}
                    />
                  ) : null}
                  <DetailInfoRow
                    label={valuationUserLabel}
                    value={
                      valuationUser ? <UserItem user={valuationUser} variant={UserItemVariant.Compact} /> : undefined
                    }
                  />
                  {valuation?.note ? (
                    <DetailInfoRow
                      label={t("masterdata:VehicleValuationNote")}
                      value={<div className="wrap-break-word whitespace-pre-wrap">{valuation.note}</div>}
                    />
                  ) : null}
                  {warningMessage ? (
                    <Alert variant="warning">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>{warningMessage}</AlertDescription>
                    </Alert>
                  ) : null}
                  {valuation?.errorMessage && !isFailedValuation ? (
                    <Alert variant="error">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>{t("masterdata:ErrorMessage")}</AlertTitle>
                      <AlertDescription>{valuation.errorMessage}</AlertDescription>
                    </Alert>
                  ) : null}

                  {shouldRenderActions ? (
                    <div className="bg-muted/20 space-y-4 rounded-lg border p-3">
                      {shouldRenderWorkflowActions ? (
                        <VehicleValuationActionGroup title={t("fab:VehicleValuationActionWorkflow")}>
                          {canStartVehicleValuationConfirmation ? (
                            <CommandButton
                              commandId="vehicle-valuation-start-confirmation"
                              label={t("masterdata:StartVehicleValuationConfirmation")}
                              variant={ButtonVariant.Filled}
                              disabled={startConfirmationMutation.isPending}
                              onClick={() => void handleStartConfirmation()}
                            />
                          ) : null}
                          {canContinueVehicleValuationConfirmation ? (
                            <>
                              <CommandButton
                                commandId="vehicle-valuation-continue-confirmation"
                                label={t("masterdata:ContinueVehicleValuationConfirmation")}
                                variant={ButtonVariant.Filled}
                                onClick={() => navigate(`~/fab/list/${valuation.uuid}/confirm`)}
                              />
                              <CommandButton
                                commandId="vehicle-valuation-reset-confirmation"
                                label={t("masterdata:ResetConfirmationAndAssignment")}
                                variant={ButtonVariant.Outlined}
                                colorVariant={ButtonColorVariant.Secondary}
                                disabled={resetConfirmationAndAssignmentMutation.isPending}
                                onClick={() =>
                                  void toastMutationStatus({
                                    mutationFn: () =>
                                      resetConfirmationAndAssignmentMutation.mutateAsync({ uuid: valuation.uuid }),
                                    successMessage: t("masterdata:VehicleValuationConfirmationReset"),
                                    errorMessage: t("masterdata:ErrorResettingVehicleValuationConfirmation"),
                                  })
                                }
                              />
                            </>
                          ) : null}
                          {canSelectVehicle && !isFailedValuation ? (
                            <CommandButton
                              commandId="vehicle-valuation-select-vehicle"
                              label={t("fab:SelectVehicle")}
                              icon={<CarFrontIcon />}
                              variant={ButtonVariant.Outlined}
                              colorVariant={ButtonColorVariant.Secondary}
                              onClick={() => navigate(`~/fab/list/${valuation.uuid}/select-vehicle`)}
                            />
                          ) : null}
                          {canStartVehicleReevaluation ? (
                            <VehicleValuationExpiryTooltip
                              isExpired={isValuationExpired}
                              reason={t("fab:VehicleValuationMustBeValidForReevaluation")}
                            >
                              <CommandButton
                                commandId="vehicle-valuation-start-reevaluation"
                                label={t("fab:StartVehicleReevaluation")}
                                variant={ButtonVariant.Outlined}
                                colorVariant={ButtonColorVariant.Secondary}
                                disabled={
                                  isValuationExpired ||
                                  isCopyForReevaluationPending ||
                                  startConfirmationMutation.isPending
                                }
                                onClick={() => void handleStartReevaluation()}
                              />
                            </VehicleValuationExpiryTooltip>
                          ) : null}
                          {canCreateVehicleBuyIn && valuation ? (
                            <VehicleValuationExpiryTooltip
                              isExpired={isValuationExpired}
                              reason={t("fab:VehicleValuationMustBeValidForBuyIn")}
                            >
                              <CommandButton
                                commandId="vehicle-valuation-create-buy-in"
                                label={t("fab:CreateVehicleBuyIn")}
                                icon={<HandCoinsIcon />}
                                variant={ButtonVariant.Filled}
                                disabled={isValuationExpired}
                                onClick={() => navigate(`~/fab/buy-in/create?vehicleValuationUuid=${valuation.uuid}`)}
                              />
                            </VehicleValuationExpiryTooltip>
                          ) : null}
                        </VehicleValuationActionGroup>
                      ) : null}

                      {shouldRenderExternalSystemActions ? (
                        <VehicleValuationActionGroup title={t("fab:VehicleValuationActionExternalSystems")}>
                          {canOpenDatCase && valuation?.datClaimId ? (
                            <CommandButton
                              commandId="vehicle-valuation-open-dat-case"
                              label={t("fab:OpenDatCase")}
                              icon={<ExternalLinkIcon />}
                              variant={ButtonVariant.Outlined}
                              colorVariant={ButtonColorVariant.Secondary}
                              onClick={() => openInNewTab(buildDatClaimUrl(valuation.datClaimId))}
                            />
                          ) : null}

                          {canSyncDatData ? (
                            <CommandButton
                              commandId="vehicle-valuation-sync-dat-data"
                              label={t("fab:GetDatData")}
                              icon={<RefreshCwIcon />}
                              variant={ButtonVariant.Outlined}
                              colorVariant={ButtonColorVariant.Secondary}
                              disabled={syncDatDataMutation.isPending}
                              onClick={() => void handleSyncDatData()}
                            />
                          ) : null}

                          {canSyncVehicleValuationToV1Gewa ? (
                            <VehicleValuationExpiryTooltip
                              isExpired={isValuationExpired}
                              reason={t("fab:VehicleValuationMustBeValidForV1GewaSync")}
                            >
                              <CommandButton
                                commandId="vehicle-valuation-sync-v1-gewa"
                                label={t("fab:SyncVehicleValuationToV1Gewa")}
                                icon={<RefreshCwIcon />}
                                variant={ButtonVariant.Outlined}
                                colorVariant={ButtonColorVariant.Secondary}
                                disabled={isValuationExpired || syncV1GewaMutation.isPending}
                                onClick={() =>
                                  vehicleRequiresBranchSelectionForV1GewaSync(valuation?.vehicle?.v1Id)
                                    ? setIsSyncV1GewaDialogOpen(true)
                                    : void handleSyncV1Gewa()
                                }
                              >
                                <RefreshCwIcon ref={syncIconRef} />
                                {t("fab:SyncVehicleValuationToV1Gewa")}
                              </CommandButton>
                            </VehicleValuationExpiryTooltip>
                          ) : null}
                        </VehicleValuationActionGroup>
                      ) : null}

                      {shouldRenderDocumentActions ? (
                        <VehicleValuationActionGroup title={t("fab:VehicleValuationActionDocumentsAndData")}>
                          {canOpenVehicleValuationReport && valuation?.reportUrl ? (
                            <CommandButton
                              commandId="vehicle-valuation-open-report"
                              label={t("masterdata:OpenValuationReport")}
                              icon={<DownloadIcon />}
                              variant={ButtonVariant.Outlined}
                              colorVariant={ButtonColorVariant.Secondary}
                              onClick={() => openInNewTab(valuation.reportUrl)}
                            />
                          ) : null}
                          {shouldShowVehicleDataRepair ? (
                            <VehicleValuationVehicleDataRepairButton
                              status={vehicleDataRepairStatusQuery.data}
                              isPending={vehicleDataRepairMutation.isPending}
                              label={t("fab:RepairVehicleData")}
                              unavailableTitle={t("fab:VehicleDataRepairUnavailable")}
                              onClick={() => void handleRepairVehicleData()}
                            />
                          ) : null}
                          {canReadVehicleValuationSnapshot && valuation && !isFailedValuation ? (
                            <VehicleValuationSnapshotAction
                              key={valuation.uuid}
                              valuationUuid={valuation.uuid}
                              showInCommandPalette
                            />
                          ) : null}
                        </VehicleValuationActionGroup>
                      ) : null}
                    </div>
                  ) : null}
                  {canSyncVehicleValuationToV1Gewa ? (
                    <VehicleValuationV1GewaSyncDialog
                      open={isSyncV1GewaDialogOpen}
                      isPending={syncV1GewaMutation.isPending}
                      onOpenChange={setIsSyncV1GewaDialogOpen}
                      onSubmit={handleSyncV1Gewa}
                    />
                  ) : null}
                </CardContent>
              </Card>
            </div>

            {canReadVehicleBuyIn && valuation?.uuid ? (
              <VehicleValuationBuyInsCard vehicleValuationUuid={valuation.uuid} />
            ) : null}
          </div>

          {isFailedValuation ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center overflow-y-auto rounded-xl border border-white/20 bg-white/30 px-6 py-8 text-center shadow-lg shadow-black/10 backdrop-blur-md dark:border-white/[0.14] dark:bg-white/5.5 dark:shadow-black/25">
              <div className="mx-auto flex max-w-2xl flex-col items-center">
                <div className="border-destructive/25 bg-destructive/10 text-destructive flex size-11 items-center justify-center rounded-full border">
                  <TriangleAlert className="size-5" />
                </div>

                <div className="mt-4 space-y-2">
                  <h2 className="text-xl font-semibold tracking-tight">{t(failureDisplay.titleKey)}</h2>
                  <p className="text-muted-foreground text-sm leading-6">{t(failureDisplay.descriptionKey)}</p>
                </div>

                {failureDisplay.detailLabelKey ? (
                  <div className="mt-10 space-y-1">
                    <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                      {t(failureDisplay.detailLabelKey)}
                    </p>
                    <code className="text-foreground block font-mono text-sm leading-6 font-semibold wrap-break-word">
                      {failureDisplay.detail}
                    </code>
                  </div>
                ) : null}

                {failureDisplay.secondaryDetail ? (
                  <p className="text-muted-foreground max-w-2xl text-sm leading-6">{failureDisplay.secondaryDetail}</p>
                ) : null}

                {(canSelectVehicle || canReadVehicleSnapshot || canReadVehicleValuationSnapshot) && valuation ? (
                  <div className="mt-6 flex flex-wrap justify-center gap-2">
                    {canSelectVehicle ? (
                      <CommandButton
                        commandId="vehicle-valuation-select-vehicle"
                        label={t("fab:SelectVehicle")}
                        icon={<CarFrontIcon />}
                        variant={ButtonVariant.Outlined}
                        colorVariant={ButtonColorVariant.Secondary}
                        onClick={() => navigate(`~/fab/list/${valuation.uuid}/select-vehicle`)}
                      />
                    ) : null}
                    {canReadVehicleSnapshot && valuation.vehicle?.uuid ? (
                      <VehicleSnapshotAction vehicleUuid={valuation.vehicle.uuid} showInCommandPalette />
                    ) : null}
                    {canReadVehicleValuationSnapshot ? (
                      <VehicleValuationSnapshotAction
                        key={valuation.uuid}
                        valuationUuid={valuation.uuid}
                        showInCommandPalette
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </CenteredLayout>
    </UuidExistenceWrapper>
  );
}
